
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import countryList from 'react-select-country-list';
import { useMemo } from 'react';
import { getStripe } from '@/lib/stripe';
import { Loader2 } from 'lucide-react';

import MuseVideoPlayer from '@/components/MuseVideoPlayer';

const formSchema = z.object({
  name: z.string().trim().min(2, 'Please enter your full name').max(100, 'Name is too long'),
  email: z.string().trim().email('Please enter a valid email address').max(254, 'Email is too long'),
  country: z.string().trim().length(2, 'Please select your country'),
});

type FormValues = z.infer<typeof formSchema>;

const HeroSection = () => {
  
  const countryOptions = useMemo(() => {
    const data = countryList().getData();
    const unitedStates = data.find((c) => c.value === 'US');
    const others = data.filter((c) => c.value !== 'US');
    return unitedStates ? [unitedStates, ...others] : data;
  }, []);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      email: '',
      country: 'US',
    },
  });

  const REGISTER_LEAD_URL = 'https://guide.workstreamer.com/api/v1/liberated-consultant/register-lead'
  const CREATE_CHECKOUT_URL = 'https://guide.workstreamer.com/api/v1/liberated-consultant/create-checkout-session'

  function getPriceId(): string {
    return (
      ((import.meta as any).env?.VITE_LIBERATED_CONSULTANT_BOOK_PRICE_ID as string | undefined) ??
      ((import.meta as any).env?.LIBERATED_CONSULTANT_BOOK_PRICE_ID as string | undefined) ??
      ''
    )
  }

  async function registerLead(payload: FormValues): Promise<string | null> {
    try {
      const res = await fetch(REGISTER_LEAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: payload.name,
          email: payload.email,
          country: payload.country,
        }),
      })
      const json = await res.json().catch(() => null)
      const userId: string | undefined = json?.user_id
      if (!userId) {
        console.error('No user id found')
        return null
      }
      return userId;
    } catch (error) {
      console.error('Failed to register lead', error)
      return null
    }
  }

  async function createCheckoutSession(userId: string): Promise<{ url?: string; sessionId?: string } | null> {
    const priceId = getPriceId()
    if (!priceId) {
      console.error('Missing price id (VITE_LIBERATED_CONSULTANT_BOOK_PRICE_ID)')
      return null
    }
    try {
      const res = await fetch(CREATE_CHECKOUT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ price_id: priceId, user_id: userId, success_path: 'upsell.html', cancel_path: '' }),
      })
      const json = await res.json().catch(() => null)
      if (!json) {
        console.error('Failed to parse checkout session response')
        return null
      }
      return { url: json?.url, sessionId: json?.sessionId ?? json?.id }
    } catch (error) {
      console.error('Failed to create checkout session', error)
      return null
    }
  }

  async function redirectToCheckout(result: { url?: string; sessionId?: string } | null) {
    if (!result) return
    if (result.url && typeof result.url === 'string') {
      window.location.href = result.url
      return
    }
    if (result.sessionId && typeof result.sessionId === 'string') {
      const stripe = await getStripe()
      await stripe?.redirectToCheckout({ sessionId: result.sessionId })
      return
    }
    console.error('No checkout session url or id returned')
  }

  const onSubmit = async (data: FormValues) => {
    const userId = await registerLead(data)
    if (!userId) return
    const session = await createCheckoutSession(userId)
    await redirectToCheckout(session)
  };


  return (
    <section className="py-12 relative bg-gradient-to-b from-book-blue via-book-blue/90 to-book-darkBlue/95">
      <div className="container mx-auto px-4 relative z-10">
        <div className="mx-auto max-w-5xl">
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6 text-center leading-tight">
            FREE Book Reveals How Enterprise Software Consultants Bring in $250K+ Salary While Working Half the Year
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-3xl mx-auto text-center leading-relaxed">
            The Liberated Consultant is the Ultimate Guide for ERP Consultants to Escape the Corporate Grind and Successfully Go Independent
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-12">
            {/* Video Section */}
            <div className="flex flex-col space-y-6">
              <div className="bg-white/95 p-2.5 rounded-lg border border-book-yellow shadow-lg">
                <div className="relative aspect-video rounded-md overflow-hidden">
                  <MuseVideoPlayer videoId="n48x2YU" className="w-full h-full" />
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <img 
                  src="/lovable-uploads/feaa465b-1403-49b4-aa92-6fe4aee328ee.png" 
                  alt="The Liberated Consultant Book" 
                  className="w-40 h-auto drop-shadow-md"
                />
                <div className="text-white/90 text-sm md:text-base leading-relaxed">
                  <p className="font-bold text-white">Inside Your Free Copy You'll Discover:</p>
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Strategies to break into the lucrative ERP consulting industry if you're new.</li>
                    <li>How to ensure you have paying work BEFORE you quit your consulting job.</li>
                    <li>The best sources for finding consistent projects & what to do when there aren't any.</li>
                    <li>Productivity hacks to multiply yourself and increase your capacity for quality work.</li>
                  </ul>
                </div>
              </div>
            </div>
            
            {/* Form Section */}
            <div id="hero-order-form" className="bg-white/95 p-6 rounded-lg border border-book-yellow shadow-xl w-full">
              <h3 className="text-xl font-bold text-book-darkBlue mb-5 text-center">
                Get Your FREE Copy Today!
              </h3>
              
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">Full Name</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Your full name" 
                            className="bg-gray-50 border-gray-200 text-gray-700 placeholder:text-gray-400 focus:border-book-yellow focus:ring-book-yellow/30" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">Email Address</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="Your email address" 
                            type="email"
                            className="bg-gray-50 border-gray-200 text-gray-700 placeholder:text-gray-400 focus:border-book-yellow focus:ring-book-yellow/30" 
                            {...field} 
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="country"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-gray-700">Country</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-700 text-base md:text-sm focus:border-book-yellow focus:ring-book-yellow/30">
                              <SelectValue placeholder="Select your country" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {countryOptions.map((opt) => (
                              <SelectItem 
                                key={opt.value} 
                                value={opt.value} 
                                className="text-gray-700 data-[highlighted]:bg-book-yellow data-[highlighted]:text-book-darkBlue"
                              >
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  <Button 
                    type="submit"
                    disabled={form.formState.isSubmitting}
                    className="w-full bg-book-yellow hover:bg-book-yellow/90 text-book-darkBlue font-bold py-4 text-base uppercase tracking-wide shadow-lg transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed">
                    {form.formState.isSubmitting && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Rush Me a Free Copy
                  </Button>
                  
                  <p className="text-book-red text-xs text-center mt-3 font-medium">
                    Just pay $9.95 for shipping & handling
                  </p>
                   <p className="text-gray-600 text-xs text-center mt-2 italic">
                     The Liberated Consultant retails for $23.95 + S/H but my software company WorkStreamer has  bought it for you!  I just ask that you cover the shipping and handling costs in order to send it to you.  Your information will be kept private and your order will be processed securely.  Limited quantities available.
                   </p>
                </form>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
